import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup, Sheet, SheetContent } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Sparkles } from 'lucide-react';
import { Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { OnlyOfficeConnectorProvider } from '../../../contexts/OnlyOfficeConnectorContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { AISidebarShell } from './AISidebarShell';
import { DevResyncBanner } from './DevResyncBanner';
import { ProposalV2Provider, useProposalV2Context } from './ProposalV2Context';
import { ConfirmDialogProvider } from './shared/ConfirmDialog';
import { WorkspaceProvider } from './workspace/WorkspaceContext';
/**
 * V2 proposal dashboard shell.
 *
 * Two-pane layout: main content (left, contains the nested route's <Outlet />)
 * + right AI sidebar. The left navigation rail is the EXISTING global
 * `Sidebar.tsx`, which already swaps to `ProjectNavItems` on `/proposal/:id`
 * routes — we don't render a second rail here.
 *
 * Responsive behavior:
 *  - Desktop (md and up): the AI sidebar is a `ResizablePanel` you can drag.
 *  - Narrow (< md):       the AI sidebar becomes a slide-in `Sheet` overlay,
 *                          and the main pane gets the full width.
 *
 * This is a layout component used at the `proposal/:id/v2` router level. The
 * nested children (HomePage, ComplianceHubPage, etc.) render via <Outlet />.
 */ export function ProposalDashboardV2() {
    const { id } = useParams();
    const { user } = useAuth();
    const chatStorageKey = `proposal-chat::${user?.id ?? 'anonymous'}::${id ?? ''}`;
    return /*#__PURE__*/ _jsx(ConfirmDialogProvider, {
        children: /*#__PURE__*/ _jsx(ProposalV2Provider, {
            storageKey: chatStorageKey,
            children: /*#__PURE__*/ _jsx(OnlyOfficeConnectorProvider, {
                children: /*#__PURE__*/ _jsx(WorkspaceProvider, {
                    projectId: id ?? '',
                    children: /*#__PURE__*/ _jsx("div", {
                        "data-testid": Testids.project.proposalDashboard,
                        className: "contents",
                        children: /*#__PURE__*/ _jsx(ProposalDashboardV2Shell, {})
                    })
                })
            })
        }, chatStorageKey)
    });
}
function ProposalDashboardV2Shell() {
    const { sidebarOpen, setSidebarOpen, toggleSidebar, activeChatId } = useProposalV2Context();
    const isDesktop = useMediaQuery('(min-width: 768px)');
    // Narrow viewport: render the main pane full-width and put the AI sidebar in a Sheet.
    if (!isDesktop) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "relative flex h-full w-full flex-col",
            children: [
                /*#__PURE__*/ _jsx(DevResyncBanner, {}),
                /*#__PURE__*/ _jsx("div", {
                    className: "min-h-0 flex-1 w-full overflow-auto bg-slate-50",
                    children: /*#__PURE__*/ _jsx(Outlet, {})
                }),
                /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    onClick: toggleSidebar,
                    className: "fixed bottom-4 right-4 z-20 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 shadow-lg hover:bg-slate-50",
                    "aria-label": "Open AI sidebar",
                    children: [
                        /*#__PURE__*/ _jsx(Sparkles, {
                            className: "h-4 w-4 text-violet-500",
                            "aria-hidden": "true"
                        }),
                        "Ask AI"
                    ]
                }),
                /*#__PURE__*/ _jsx(Sheet, {
                    open: sidebarOpen,
                    onOpenChange: setSidebarOpen,
                    children: /*#__PURE__*/ _jsx(SheetContent, {
                        side: "right",
                        // Override sheet defaults: full width up to a sane max, and zero
                        // padding so AISidebarShell controls its own layout.
                        className: "w-[92vw] max-w-md border-0 p-0 sm:!max-w-md",
                        children: /*#__PURE__*/ _jsx(AISidebarShell, {
                            embedded: true
                        }, activeChatId)
                    })
                })
            ]
        });
    }
    // Desktop layout: resizable two-pane.
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative flex h-full w-full flex-col",
        children: [
            /*#__PURE__*/ _jsx(DevResyncBanner, {}),
            !sidebarOpen && /*#__PURE__*/ _jsx("div", {
                className: "flex shrink-0 justify-end px-4 py-2",
                children: /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    onClick: toggleSidebar,
                    className: "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50",
                    "aria-label": "Open AI sidebar",
                    children: [
                        /*#__PURE__*/ _jsx(Sparkles, {
                            className: "h-3.5 w-3.5 text-violet-500",
                            "aria-hidden": "true"
                        }),
                        "Ask AI"
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(ResizablePanelGroup, {
                direction: "horizontal",
                className: "min-h-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx(ResizablePanel, {
                        defaultSize: sidebarOpen ? 70 : 100,
                        minSize: 40,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "h-full overflow-auto bg-slate-50",
                            children: /*#__PURE__*/ _jsx(Outlet, {})
                        })
                    }),
                    sidebarOpen && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ResizableHandle, {
                                withHandle: true
                            }),
                            /*#__PURE__*/ _jsx(ResizablePanel, {
                                defaultSize: 30,
                                minSize: 20,
                                maxSize: 50,
                                children: /*#__PURE__*/ _jsx(AISidebarShell, {}, activeChatId)
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
